import { Fetcher } from '@this/src/util';
import type {
  FeedbackType,
  RawCalendarConnectionPrompt,
  RawCalendarContextSummary,
  Direction,
  MessageType,
  RawSearchSupportInsight,
  RawSummary,
  RawUnsupportedRequest
} from '../types/messages';
import { toChatMessage } from '../utils/messageNormalizer';

export type { FeedbackType };

type ChatMessageResponse = {
  id: string;
  trip_id: number;
  direction: Direction;
  text: string;
  created_at: string;
  type?: MessageType;
  bundle_ids?: number[];
  item_ids?: number[];
  summary?: RawSummary;
  insight?: RawSearchSupportInsight;
  unsupported_request?: RawUnsupportedRequest;
  calendar_connection_prompt?: RawCalendarConnectionPrompt;
  calendar_context_summary?: RawCalendarContextSummary;
  editable?: boolean;
  input_disabled?: boolean;
  proposal_change_disabled?: boolean;
  feedback?: FeedbackType;
};

type FetchAiMessagesResponse = {
  messages: ChatMessageResponse[];
};

type SendAiMessageResponse = {
  user_message: ChatMessageResponse;
};

export type RecentMessagePayload = {
  direction: Direction;
  text: string;
};

type CreateDraftResponse = {
  trip_id: number;
};

export const fetchAiMessages = async (tripId: string) => {
  const response = await Fetcher.get<FetchAiMessagesResponse>(`/trips/${tripId}/ai_messages.json`);
  return response.messages.map(message => toChatMessage(message));
};

export const sendAiMessage = async (tripId: string, text: string, recentMessages: RecentMessagePayload[]) => {
  const response = await Fetcher.post<SendAiMessageResponse>(`/trips/${tripId}/ai_messages`, {
    ai_message: { text, recent_messages: recentMessages }
  });

  return toChatMessage(response.user_message);
};

type UpdateAiMessageFeedbackResponseArgs = {
  id: number | string;
  feedback: FeedbackType;
};

export const updateAiMessageFeedback = async (
  tripId: string,
  messageId: string,
  feedbackType: FeedbackType
): Promise<UpdateAiMessageFeedbackResponseArgs> => {
  const rawId = messageId.replace(/^ai-/, '');
  return Fetcher.patch<UpdateAiMessageFeedbackResponseArgs>(`/trips/${tripId}/ai_messages/${rawId}/feedback`, {
    feedback_type: feedbackType
  });
};

export const createDraftTripWithMessage = async (text: string, recentMessages: RecentMessagePayload[]) => {
  const response = await Fetcher.post<CreateDraftResponse>('/trips/ai_messages', {
    ai_message: { text, recent_messages: recentMessages }
  });

  return {
    tripId: response.trip_id.toString()
  };
};
